import PropTypes from 'prop-types';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import {
  Button,
  IconButton,
  Link,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

import { openUrlInNewTab } from '../../utils';
import { copyTextToClipboard } from '../Shared/utils/copyToClipboard';

import { copyIcon, pinIcon } from '../../res/svg/icons';

export const GoogleButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.custom.google,
  color: theme.palette.common.white,
  width: '100%',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: alpha(theme.palette.custom.google, 0.9),
  },
}));

export const FacebookButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: '#3c4043',
  border: `1px solid #dadce0`,
  borderRadius: '20px',
  width: '100%',
  height: '40px',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: '#4285f40a',
    borderColor: '#d2e3fc',
  },
  '& svg': {
    color: theme.palette.custom.facebook,
  },
}));

export const AppleButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  color: '#3c4043',
  border: `1px solid #dadce0`,
  borderRadius: '20px',
  width: '100%',
  height: '40px',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: '#4285f40a',
    borderColor: '#d2e3fc',
  },
}));

export const StatusButton = styled(Button)(({ theme }) => ({
  width: '100%',
  color: theme.palette.common.white,
  fontWeight: theme.typography.fontWeightRegular,
}));

export const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  color: theme.palette.common.white,
  fontWeight: theme.typography.fontWeightMedium,
  '&:hover': {
    backgroundColor: theme.palette.success.dark,
  },
  '&:disabled': {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.success.light,
  },
}));

export const CallCenterStyledButton = ({ children, ...rest }) => (
  <Button
    type="button"
    variant="contained"
    disableElevation
    fullWidth
    {...rest}
  >
    {children}
  </Button>
);

CallCenterStyledButton.propTypes = {
  children: PropTypes.node.isRequired,
};

export const CallCenterInfoButton = styled(CallCenterStyledButton)(
  ({ theme }) => ({
    backgroundColor: theme.palette.custom.primary2,
  }),
);

export const CallCenterDoorButton = styled(CallCenterStyledButton)(
  ({ theme }) => ({
    backgroundColor: theme.palette.grey[500],
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.grey[600],
    },
  }),
);

export const PinButton = ({ onClick, className, sx }) => (
  <IconButton sx={sx} className={className} size="small" onClick={onClick}>
    <SvgIcon
      style={{ transform: 'rotate(45deg)' }}
      viewBox="0 0 24 24"
      fontSize="medium"
    >
      {pinIcon}
    </SvgIcon>
  </IconButton>
);

PinButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  className: PropTypes.string,
  sx: PropTypes.instanceOf(Object),
};

export const ButtonLink = ({ children, disabled, onClick, ...rest }) => {
  if (!children) return null;
  if (disabled) return <Typography {...rest}>{children}</Typography>;
  return (
    <Link component="button" variant="body1" onClick={onClick} {...rest}>
      {children}
    </Link>
  );
};

ButtonLink.defaultProps = {
  children: null,
  disabled: false,
};

ButtonLink.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export const CopyToClipBoardButton = (props) => {
  const { text, classes } = props;
  return (
    <Tooltip title="Copy" placement="right">
      <IconButton
        className={classes}
        size="small"
        onClick={() => copyTextToClipboard(text)}
      >
        <SvgIcon viewBox="0 0 512 512" fontSize="small">
          {copyIcon}
        </SvgIcon>
      </IconButton>
    </Tooltip>
  );
};

CopyToClipBoardButton.defaultProps = {
  classes: '',
};

CopyToClipBoardButton.propTypes = {
  text: PropTypes.string.isRequired,
  classes: PropTypes.string,
};

export const OpenInNewTabButton = (props) => {
  const { url, tooltip, classes } = props;
  return (
    <Tooltip title={tooltip} placement="right">
      <IconButton
        className={classes}
        size="small"
        onClick={() => openUrlInNewTab(url)}
      >
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

OpenInNewTabButton.defaultProps = {
  classes: '',
};

OpenInNewTabButton.propTypes = {
  url: PropTypes.string.isRequired,
  tooltip: PropTypes.string.isRequired,
  classes: PropTypes.string,
};

export const SwitchAgentButton = (props) => {
  const { onClick, classes = '' } = props;
  return (
    <Tooltip title="Switch Agent" placement="right">
      <IconButton className={classes} size="small" onClick={onClick}>
        <SwitchAccountIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

SwitchAgentButton.defaultProps = {
  classes: '',
};

SwitchAgentButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  classes: PropTypes.string,
};
